import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";

interface DialogContentProps
    extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
    wide?: boolean;
    dark?: boolean;
}

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn("fixed inset-0 z-40 bg-white/80", className)}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    DialogContentProps
>(({ className, children, wide, dark = true, ...props }, ref) => {
    // The app's persistent left nav (UserLayout.tsx) is `fixed left-0 w-16`
    // (64px) with z-index 999 — above this portaled dialog's z-50 — and
    // page content clears it via `ml-16`. A `wide` dialog can render nearly
    // edge-to-edge at in-between desktop widths (~1440px, before the plan
    // grid inside it has enough slack to center itself away from the edge),
    // putting its own left edge, and thus its content, behind the sidebar.
    // Only `wide` needs this: the default max-w-4xl dialog never gets close
    // to the viewport edge. The sidebar is bottom-fixed on mobile instead,
    // so no offset is needed there — same threshold UserLayout uses.
    const isMobile = useIsMobile(900);
    return (
        <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay
                className={`fixed inset-0 z-50 ${
                    dark ? "bg-black bg-opacity-50" : "bg-white"
                }`}
            />
            {/* Wrapper div for centering and height control */}
            <div
                className={`fixed inset-0 z-50 flex items-center justify-center px-4 py-4 ${
                    wide && !isMobile ? "pl-20" : ""
                }`}
            >
                <DialogPrimitive.Content
                    ref={ref}
                    className={cn(
                        "w-full bg-background rounded-lg shadow-lg overflow-y-auto overflow-x-hidden max-h-[calc(90dvh-2rem)] p-6",
                        wide ? "max-full" : "max-w-4xl",
                        className
                    )}
                    {...props}
                >
                    {children}
                </DialogPrimitive.Content>
            </div>
        </DialogPrimitive.Portal>
    );
});

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col space-y-1.5 text-center sm:text-left",
            className
        )}
        {...props}
    />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
            className
        )}
        {...props}
    />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn(
            "text-xl text-primary font-bold leading-none tracking-tight",
            className
        )}
        {...props}
    />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
    />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogTrigger,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
